import { useTipStore } from '@/store/TipStore/TipStore';
import {
  useGetTipsCategories,
  useGetTipsByCategory,
  useAddTip,
  useUpdateTip,
  useRemoveTip,
} from '@/api/hooks/useTip';
import { useTipByCategoryForm } from '@/hooks/useTipForm';
import { useAddTipByCategory } from '@/api/hooks/useTip';
import { CreateTipsCategoryInput, TipOutput, TipType } from '@/api/gql/graphql';
import { useToast } from '@/hooks/useToast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TipFormValues } from '@/hooks/useTipForm';
import { Options } from '@/components/Common/Selects/Select.types';
import axios from 'axios';
import { createFile } from '@/api/fileUpload';
import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';

type PreparedTipData = {
  category: string;
  title: string;
  audio: string | undefined;
  description: string;
  content: { html: string | undefined; text: string | undefined };
  type: TipType;
  isPublished: boolean;
};

export const useTipsList = () => {
  const { showToast } = useToast();
  const {
    selectedTip,
    setSelectedTip,
    tip,
    setTip,
    isEditMode,
    setIsEditMode,
    isOpenModal,
    setIsOpenModal,
    isOpenForm,
    setIsOpenForm,
  } = useTipStore();

  const {
    handleSubmitTipByCategory,
    registerTipByCategory,
    errorsTipByCategory,
    resetTipByCategory,
    handleSubmitTip,
    registerTip,
    errorsTip,
    resetTip,
    setValueTip,
    control,
  } = useTipByCategoryForm();

  const { data: tipsCategories } = useGetTipsCategories();
  const { data: tipsByCategory } = useGetTipsByCategory(selectedTip?._id ?? '');
  const { mutate: addTipByCategory, isPending: isAddingTipByCategory } =
    useAddTipByCategory();
  const { mutate: addTip, isPending: isCreatingTip } = useAddTip();
  const { mutate: updateTip, isPending: isUpdatingTip } = useUpdateTip();
  const { mutate: removeTip, isPending: isRemovingTip } = useRemoveTip();

  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [tipToTogglePublish, setTipToTogglePublish] =
    useState<TipOutput | null>(null);
  const [isCreatePublishModalOpen, setIsCreatePublishModalOpen] =
    useState(false);
  const [pendingPublishData, setPendingPublishData] =
    useState<PreparedTipData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tipToDelete, setTipToDelete] = useState<string | null>(null);
  const [generateUrlLoading, setGenerateUrlLoading] = useState<boolean>(false);
  const [tipType, setTipType] = useState<TipType>(TipType.Text);

  const handleCloseForm = () => {
    resetTip();
    setValueTip('category', '');
    setIsEditMode(false);
    setIsOpenForm(false);
    setGenerateUrlLoading(false);
  };

  const handleTipTypeChange = (value: Options) => {
    if (value && value._id) {
      setTipType(value._id as TipType);
    }
  };

  const addTipByCategoryHandler = (data: CreateTipsCategoryInput) => {
    addTipByCategory(data, {
      onSuccess: () => {
        resetTipByCategory();
        setIsOpenModal(false);
        showToast('Catégorie créée avec succès', 'success');
      },
      onError: (error) => {
        showToast(error.message, 'error');
      },
    });
  };

  const prepareTipData = useCallback(
    async (data: TipFormValues, isPublished: boolean) => {
      const token = localStorage.getItem('authToken') ?? '';
      let audioUrl: string | undefined = undefined;
      if (data.audioFile) {
        setGenerateUrlLoading(true);
        try {
          const response = await axios(createFile(data.audioFile, token));
          if (response.status === 201) {
            audioUrl = response.data?.publicUrl || response.data;
          } else {
            throw new Error('Erreur lors du téléchargement du fichier audio');
          }
        } catch (error) {
          console.error(
            'Erreur lors du téléchargement du fichier audio:',
            error,
          );
          throw error;
        } finally {
          setGenerateUrlLoading(false);
        }
      }

      return {
        category: data.category,
        title: data.title,
        audio: audioUrl,
        description: data.description,
        content: {
          html: data.text,
          text: data.text,
        },
        type: tipType?.toUpperCase() as TipType,
        isPublished,
      };
    },
    [tipType],
  );

  const handleDraftTip = handleSubmitTip(async (data: TipFormValues) => {
    try {
      const preparedData = await prepareTipData(data, false);
      addTip(preparedData, {
        onSuccess: () => {
          showToast('Conseil créé avec succès', 'success');
          handleCloseForm();
        },
        onError: (error) => {
          showToast(error.message, 'error');
        },
      });
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : 'Erreur lors de la préparation des données',
        'error',
      );
    }
  });

  const handleDraftUpdateTip = handleSubmitTip(async (data: TipFormValues) => {
    if (tip) {
      try {
        const preparedData = await prepareTipData(data, tip.isPublished);
        updateTip(
          {
            id: tip._id,
            input: preparedData,
          },
          {
            onSuccess: () => {
              showToast('Conseil modifié avec succès', 'success');
              handleCloseForm();
            },
            onError: (error) => {
              showToast(error.message, 'error');
            },
          },
        );
      } catch (error) {
        showToast(
          error instanceof Error
            ? error.message
            : 'Erreur lors de la préparation des données',
          'error',
        );
      }
    }
  });

  const handleCreateAndPublishTip = handleSubmitTip(
    async (data: TipFormValues) => {
      try {
        const preparedData = await prepareTipData(data, true);
        setPendingPublishData(preparedData);
        setIsCreatePublishModalOpen(true);
      } catch (error) {
        showToast(
          error instanceof Error
            ? error.message
            : 'Erreur lors de la préparation des données',
          'error',
        );
      }
    },
  );

  const handleConfirmCreateAndPublish = () => {
    if (pendingPublishData) {
      if (isEditMode && tip) {
        updateTip(
          {
            id: tip._id,
            input: pendingPublishData,
          },
          {
            onSuccess: () => {
              showToast(
                tip.isPublished
                  ? 'Conseil masqué avec succès'
                  : 'Conseil publié avec succès',
                'success',
              );
              handleCloseForm();
              setIsCreatePublishModalOpen(false);
              setPendingPublishData(null);
            },
            onError: (error) => {
              showToast(error.message, 'error');
              return;
            },
          },
        );
      } else {
        addTip(pendingPublishData, {
          onSuccess: () => {
            showToast('Conseil publié avec succès', 'success');
            handleCloseForm();
            setIsCreatePublishModalOpen(false);
            setPendingPublishData(null);
          },
          onError: (error) => {
            showToast(error.message, 'error');
          },
        });
      }
    }
  };

  const handleTogglePublish = (tip: TipOutput) => {
    setTipToTogglePublish(tip);
    setIsPublishModalOpen(true);
  };

  const handleConfirmPublish = () => {
    if (tipToTogglePublish) {
      updateTip(
        {
          id: tipToTogglePublish._id,
          input: {
            category: tipToTogglePublish.category?._id,
            title: tipToTogglePublish.title,
            description: tipToTogglePublish.description,
            type: tipToTogglePublish.type,
            content: tipToTogglePublish.content,
            audio: tipToTogglePublish.audio,
            isPublished: !tipToTogglePublish.isPublished,
          },
        },
        {
          onSuccess: () => {
            showToast(
              tipToTogglePublish.isPublished
                ? 'Conseil masqué avec succès'
                : 'Conseil publié avec succès',
              'success',
            );
            setIsPublishModalOpen(false);
            setTipToTogglePublish(null);
          },
          onError: (error) => {
            showToast(error.message, 'error');
          },
        },
      );
    }
  };

  const handleDeleteTip = (tipId: string) => {
    setTipToDelete(tipId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (tipToDelete) {
      removeTip(tipToDelete, {
        onSuccess: () => {
          showToast('Conseil supprimé avec succès', 'success');
          setIsDeleteModalOpen(false);
          setTipToDelete(null);
        },
        onError: (error) => {
          showToast(error.message, 'error');
        },
      });
    }
  };

  const menuItems = useCallback(
    (tip: TipOutput) => [
      createMenuItem('Modifier', '', () => handleEditTip(tip)),
      createMenuItem(`${tip?.isPublished ? 'Cacher' : 'Publier'}`, '', () =>
        handleTogglePublish(tip),
      ),
      createMenuItem('Supprimer', 'font-[400] text-[#EA242C]', () =>
        handleDeleteTip(tip._id),
      ),
    ],
    [],
  );

  const handleEditTip = (tip: TipOutput) => {
    setTip(tip);
    setIsEditMode(true);
    setIsOpenForm(true);
  };

  const handleStatusFilterChange = (values: Options[]) => {
    setStatusFilter(values.map((v) => String(v._id)));
  };

  const filteredTips = useMemo(() => {
    if (!tipsByCategory) return [];

    if (!statusFilter.length) return tipsByCategory;

    return tipsByCategory.filter((tip) => {
      const tipStatus = String(tip.isPublished);
      return statusFilter.includes(tipStatus);
    });
  }, [tipsByCategory, statusFilter]);

  useEffect(() => {
    if (tipsCategories && tipsCategories.length > 0 && !selectedTip) {
      setSelectedTip(tipsCategories[0]);
    }
  }, [tipsCategories, selectedTip]);

  const getPublishButtonText = () => {
    if (isEditMode) {
      return tip?.isPublished ? 'Cacher' : 'Publier';
    }
    return 'Publier';
  };

  const getModalTitle = () => {
    if (isEditMode && tip?.isPublished) {
      return 'Confirmation de masquage';
    }
    return 'Confirmation de publication';
  };

  const getModalMessage = () => {
    if (isEditMode && tip?.isPublished) {
      return 'Êtes-vous sûr de vouloir masquer ce conseil ?';
    }
    return 'Êtes-vous sûr de vouloir publier ce conseil ?';
  };

  return {
    handleSubmitTipByCategory,
    registerTipByCategory,
    errorsTipByCategory,
    resetTipByCategory,
    registerTip,
    errorsTip,
    resetTip,
    setValueTip,
    control,
    selectedTip,
    setSelectedTip,
    addTipByCategoryHandler,
    isAddingTipByCategory,
    isOpenModal,
    setIsOpenModal,
    isOpenForm,
    setIsOpenForm,
    tipsCategories,
    tipsByCategory,
    isEditMode,
    setIsEditMode,
    tip,
    setTip,
    tipType,
    setTipType,
    handleTipTypeChange,
    handleCloseForm,
    handleDraftTip,
    handleDraftUpdateTip,
    handleCreateAndPublishTip,
    handleConfirmCreateAndPublish,
    isCreatePublishModalOpen,
    setIsCreatePublishModalOpen,
    handleTogglePublish,
    handleConfirmPublish,
    isPublishModalOpen,
    setIsPublishModalOpen,
    handleEditTip,
    handleStatusFilterChange,
    filteredTips,
    statusFilter,
    getPublishButtonText,
    getModalTitle,
    getModalMessage,
    isCreatingTip,
    isRemovingTip,
    menuItems,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleConfirmDelete,
    tipToTogglePublish,
    isUpdatingTip,
    generateUrlLoading,
  };
};
